import React, { ReactNode, useEffect, useRef, useState } from 'react'
import throttle from 'lodash.throttle'
import { subDays } from 'date-fns'
import cn from 'classnames'

import { ArrowRightIcon, Spinner } from 'components'
import { isDef, useInfiniteScroll } from 'utils'
import { ChartEvents } from 'stores/ChartStore/types'
import { Nullable } from 'types'

import { useSwipe } from './hooks'
import {
  Days,
  LongTermEvents,
  OneTimeEvents,
  PremiumInfo,
  TimeLabels,
  TimeLines,
} from './components'
import {
  dateFormat,
  dayWidth,
  eventsMap,
  gapWidth,
  intervals,
  lineGapY,
  margin,
  premiumRight,
  shadowOffset,
  timeFormat,
  timeLabelsWidth,
} from './constants'
import { calculateCellHeight, checkScrollPosition } from './utils'
import './styles.scss'

interface Props {
  events: Nullable<ChartEvents>
  daysToShow: number
  onLoadMore: () => void
  loading?: boolean
  premium?: boolean
  parentHeight: number
}

export const EventsChart = (props: Props) => {
  const { events, daysToShow, parentHeight, onLoadMore, loading = false, premium } = props
  const [tooltip, setTooltip] = useState<Nullable<{ x: number; y: number; text: ReactNode }>>(null)
  const [showBackButton, setShowBackButton] = useState(false)
  const [hasScroll, setHasScroll] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const [premiumLeft, setPremiumLeft] = useState(0)

  const throttledLoadMoreRef = useRef(
    throttle(onLoadMore, 1000, { leading: false, trailing: true }),
  )
  const throttledLoadMore = throttledLoadMoreRef.current

  useEffect(() => {
    throttledLoadMoreRef.current = throttle(onLoadMore, 1000, { leading: false, trailing: true })
  }, [onLoadMore])

  const cellHeight = calculateCellHeight(parentHeight, intervals)

  // Переводим дату в пиксели (в рамках одного дня)
  const timeToPixels = (date: Date) => {
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const hourHeight = cellHeight

    return hours * hourHeight + (minutes / 60) * hourHeight + lineGapY
  }

  const today = new Date()
  const gapsWidth = (daysToShow - 1) * gapWidth
  const svgWidth = margin + daysToShow * dayWidth + gapsWidth
  const svgHeight = intervals * cellHeight
  const days = Array.from({ length: daysToShow }, (_, i) => subDays(today, i)).reverse()

  const onShowTooltip = (e: React.MouseEvent, text: ReactNode) => {
    const target = e.target as SVGElement
    const isSingleEvent = target.tagName === 'circle'

    const rect = target.getBoundingClientRect()
    const offset = isSingleEvent ? 40 : 66

    const x = rect.left + rect.width / 2
    const y = rect.top - offset

    setTooltip({ x, y, text })
  }

  const onHideTooltip = () => setTooltip(null)

  const onScroll = useInfiniteScroll(loading, onLoadMore, { vertical: false, reverse: true })
  const onScrollHandler = (e: React.UIEvent<HTMLElement>) => {
    if (!premium) {
      return
    }

    if (tooltip) {
      onHideTooltip()
    }

    setShowBackButton(checkScrollPosition(ref.current))
    onScroll(e)
  }

  const onWheelOrTouch = (
    e: React.WheelEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    if (!ref.current || !premium) return

    const hasHorizontalScroll = ref.current?.scrollWidth > ref.current?.clientWidth

    if (!hasHorizontalScroll) {
      throttledLoadMore()
    } else if (ref.current.scrollLeft === 0) {
      // Ставим target на элемент графика, потому что иначе
      // туда могут попадать дочерние элементы, и логика скролла ломается
      const modifiedEvent = {
        ...e,
        target: ref.current,
      }

      onScrollHandler(modifiedEvent)
    }

    if ('deltaY' in e) {
      ref.current.scrollLeft += e.deltaY
    }
  }

  const onReturnToStart = () => {
    if (ref.current) {
      ref.current.scrollTo({ left: ref.current.scrollWidth, behavior: 'smooth' })
    }
  }

  const swipeHandlers = useSwipe(ref, onWheelOrTouch, premium)

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollLeft = ref.current.scrollWidth
    }
  }, [])

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollLeft = ref.current.scrollWidth
    }
  }, [])

  useEffect(() => {
    if (premium || !ref.current) {
      return
    }

    setPremiumLeft(ref.current?.scrollLeft + margin + timeLabelsWidth + shadowOffset)

    const onResize = () => {
      if (ref.current && !premium) {
        ref.current.scrollLeft = ref.current.scrollWidth
        setPremiumLeft(ref.current?.scrollLeft + margin + timeLabelsWidth + shadowOffset)
      }
    }

    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }, [premium, svgWidth])

  useEffect(() => {
    if (ref.current) {
      const hasHorizontalScroll = ref.current?.scrollWidth > ref.current?.clientWidth

      if (hasHorizontalScroll && !hasScroll) {
        ref.current.scrollLeft = ref.current.scrollWidth
      }

      setHasScroll(hasHorizontalScroll)
    }
  }, [events, hasScroll])

  useEffect(() => {
    if (isDef(events)) {
      setShowBackButton(checkScrollPosition(ref.current))
    }
  }, [events])

  return (
    <div
      ref={ref}
      className={cn('events-chart', { 'no-premium': !premium })}
      onScroll={onScrollHandler}
      onWheel={onWheelOrTouch}
      onTouchMove={onWheelOrTouch}
      {...swipeHandlers}
    >
      <Spinner variant="sm" open={loading} className="chart-spinner" />
      {showBackButton && premium && (
        <ArrowRightIcon className="back-arrow" onClick={onReturnToStart} />
      )}
      <Days days={days} />

      <div className="chart-inner" style={{ width: svgWidth + timeLabelsWidth }}>
        {!premium && <PremiumInfo style={{ left: premiumLeft, right: premiumRight }} />}
        <TimeLabels
          width={timeLabelsWidth}
          height={svgHeight}
          intervals={intervals}
          cellHeight={cellHeight}
        />

        <svg width={svgWidth} height={svgHeight}>
          <defs>
            <filter id="drop-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="1" />
              <feOffset dx="0" dy="1" result="offsetblur" />
              <feFlood floodColor="rgba(0, 0, 0, 0.2)" />
              <feComposite in2="offsetblur" operator="in" />
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <TimeLines
            margin={margin}
            intervals={intervals}
            cellHeight={cellHeight}
            lineGapY={lineGapY}
            daysToShow={daysToShow}
            dayWidth={dayWidth}
            gapsWidth={gapsWidth}
          />
          <LongTermEvents
            days={days}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            events={events?.longTerm}
            eventsMap={eventsMap}
            timeToPixels={timeToPixels}
            margin={margin}
            dayWidth={dayWidth}
            gapWidth={gapWidth}
            cellHeight={cellHeight}
            onShowTooltip={onShowTooltip}
            onHideTooltip={onHideTooltip}
          />
          <OneTimeEvents
            days={days}
            events={events?.oneTime}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            eventsMap={eventsMap}
            timeToPixels={timeToPixels}
            margin={margin}
            dayWidth={dayWidth}
            gapWidth={gapWidth}
            onShowTooltip={onShowTooltip}
            onHideTooltip={onHideTooltip}
          />
        </svg>

        {tooltip && (
          <div className="tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
            {tooltip.text}
          </div>
        )}
      </div>
    </div>
  )
}
